/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, ICoreContext } from '@msdyn365-commerce/core';
import { Button } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { ReturnOrderProduct } from '../order-return-initiate-product.component';
import { OrderReturnInitiateTableRowComponent } from './components/order-return-initiate-table-row.component';
import { IOrderReturnInitiateResources } from '../../order-return-initiate.props.autogenerated';

interface IOrderReturnInitiateTableData {
    returnOrderProducts: ReturnOrderProduct[];
    salesOrderId: string;
}

export interface IOrderReturnInitiateTableProps extends IComponentProps<IOrderReturnInitiateTableData> {
    context: ICoreContext;
    className: string;

    resources: IOrderReturnInitiateResources;
    onChange(): void;
}

export interface IOrderReturnInitiateTableComponent extends IComponent<IOrderReturnInitiateTableProps> {}

export const OrderReturnInitiateTableComponent: React.FC<IOrderReturnInitiateTableProps> = (props: IOrderReturnInitiateTableProps) => {

    const rowClassName = `${props.className}__row`;

    const switchCheckedState = (selected: boolean) => {
        props.data.returnOrderProducts.forEach(product => {
            // Only eligible lines can be (un)marked; mirror the per-row quantity behaviour.
            if (!product.isEligible) {
                return;
            }
            product.isSelected = selected;
            product.itemReturnQuantity = selected ? product.eligibleReturnQty : 0;
        });
        props.onChange();
    };

    return (
        <div className={props.className}>
            <div className={`${props.className}__order-number`}>
                {`${props.resources.returnOrderNumberLabel}${props.data.salesOrderId}`}
            </div>
            <div className={`${props.className}__controls`}>
                <Button className={`${props.className}__markAll`}
                    onClick={() => switchCheckedState(true)}
                >
                    {props.resources.markAllControlLabel}
                </Button>
                <Button className={`${props.className}__unmarkAll`}
                    onClick={() => switchCheckedState(false)}
                >
                    {props.resources.unmarkAllControlLabel}
                </Button>
            </div>
            <table className={`${props.className}__body`}>
                {props.data.returnOrderProducts.map((returnOrderProduct, index) => {
                    return (
                        <OrderReturnInitiateTableRowComponent
                            context={props.context}
                            typeName={props.typeName}
                            id={`${rowClassName}-${index}`}
                            key={returnOrderProduct.simpleProduct.ItemId}
                            className={rowClassName}
                            resources={props.resources}
                            data={{
                                salesLine: returnOrderProduct.salesLine,
                                returnProduct: returnOrderProduct
                            }}
                            onChange={props.onChange}
                        />
                    );
                })}
            </table>
        </div>
    );
};
